import {booleanAttribute, Directive, forwardRef, Input, OnChanges, SimpleChanges} from '@angular/core';
import {AbstractControl, NG_VALIDATORS, ValidationErrors, Validator} from '@angular/forms';
import {normalizedLength} from './text-length';

/**
 * Mindestlaenge eines Textfeldes auf der um Rand-Leerzeichen gekuerzten Laenge.
 *
 * Ersetzt `minlength` dort, wo die Rohlaenge mit Leerzeichen aufgefuellt werden
 * koennte. Der Fehlerschluessel bleibt `minlength`, damit bestehende
 * `mat-error`-Zweige unveraendert greifen; ein Wert aus ausschliesslich
 * Leerzeichen meldet `required`, weil er fachlich fehlt und nicht zu kurz ist.
 * Ohne `appTrimmedMin` bleibt genau diese Whitespace-Pruefung uebrig — das
 * Gegenstueck zu Angulars `required`, das `"   "` durchlaesst.
 *
 * `appCollapseWhitespace` zaehlt zusaetzlich jede Folge von Leerraum als ein
 * Zeichen. Das ist die schaerfere Regel als die des Servers und deshalb nur dort
 * gesetzt, wo sie abgestimmt ist — heute der Aufgabentitel, dessen Feld den Wert
 * beim Verlassen ebenso zusammenzieht.
 *
 * Die Hoechstlaenge bleibt bewusst bei Angulars `maxlength` auf der Rohlaenge:
 * das native Attribut begrenzt die Eingabe ohnehin ungetrimmt.
 */
@Directive({
  selector: '[appTrimmedLength]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TrimmedLengthDirective),
      multi: true,
    },
  ],
})
export class TrimmedLengthDirective implements Validator, OnChanges {
  @Input() appTrimmedMin?: number;
  @Input({transform: booleanAttribute}) appCollapseWhitespace = false;

  private onValidatorChange?: () => void;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appTrimmedMin'] || changes['appCollapseWhitespace']) {
      this.onValidatorChange?.();
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value: unknown = control.value;
    if (typeof value !== 'string' || value.length === 0) return null;

    const length = this.appCollapseWhitespace ? normalizedLength(value) : value.trim().length;
    if (length === 0) return {required: true};

    if (this.appTrimmedMin !== undefined && length < this.appTrimmedMin) {
      return {minlength: {requiredLength: this.appTrimmedMin, actualLength: length}};
    }

    return null;
  }
}
